// src/pages/AdminComunicacoes.jsx

import React, { useEffect, useRef, useState } from "react";

import {
    Box,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Checkbox,
    Divider,
    MenuItem,
    IconButton,
    Avatar,
    Tooltip,
    Snackbar,
    Alert
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DraftsRoundedIcon from "@mui/icons-material/DraftsRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

import { Editor } from "@tinymce/tinymce-react";

import {
    collection,
    addDoc,
    getDocs,
    query,
    orderBy,
    serverTimestamp,
    updateDoc,
    deleteDoc,
    doc,
} from "firebase/firestore";

import { db } from "../contexts/firebase/config";

const CLOUD_FUNCTION_URL =
    "https://us-central1-gestao-comissoes-sintanet.cloudfunctions.net/enviarComunicacao";

function formatarData(data) {
    if (!data) return "-";

    try {
        if (typeof data.toDate === "function") {
            return data.toDate().toLocaleDateString("pt-PT");
        }

        return new Date(data).toLocaleDateString("pt-PT");
    } catch (erro) {
        return "-";
    }
}

function nomeDestinatarios(tipo) {
    if (tipo === "todosClientes") return "Todos os Clientes";
    if (tipo === "todasEmpresas") return "Todas as Empresas";
    if (tipo === "manual") return "Seleção Manual";
    return tipo || "-";
}

function corEstado(estado) {
    if (estado === "enviado") return "success";
    if (estado === "rascunho") return "warning";
    if (estado === "a_enviar") return "info";
    return "error";
}

function textoEstado(estado) {
    if (estado === "enviado") return "Enviado";
    if (estado === "rascunho") return "Rascunho";
    if (estado === "a_enviar") return "A enviar";
    if (estado === "enviado_com_erros") return "Enviado com erros";
    return "Erro";
}

export default function AdminComunicacoes() {
    const [comunicacoes, setComunicacoes] = useState([]);
    const [listaDestinatarios, setListaDestinatarios] = useState([]);
    const [destinatariosSelecionados, setDestinatariosSelecionados] = useState([]);

    const [open, setOpen] = useState(false);
    const [openConfirmacao, setOpenConfirmacao] = useState(false);
    const [openVisualizar, setOpenVisualizar] = useState(false);
    const [openEliminar, setOpenEliminar] = useState(false);

    const [comunicacaoSelecionada, setComunicacaoSelecionada] = useState(null);
    const [comunicacaoEliminar, setComunicacaoEliminar] = useState(null);

    const [modoEdicao, setModoEdicao] = useState(false);
    const [idEdicao, setIdEdicao] = useState(null);

    const [assunto, setAssunto] = useState("");
    const [mensagem, setMensagem] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");
    const [destinatarios, setDestinatarios] = useState("todosClientes");
    const [pesquisaDestinatario, setPesquisaDestinatario] = useState("");
    const [htmlTemplate, setHtmlTemplate] = useState("");

    const [estadoFiltro, setEstadoFiltro] = useState("todos");
    const [pesquisa, setPesquisa] = useState("");

    const inputFile = useRef(null);

    const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
    });

    useEffect(() => {
        carregarComunicacoes();
        carregarDestinatarios();
    }, []);

    async function carregarComunicacoes() {
        try {
            const q = query(
                collection(db, "comunicacoes"),
                orderBy("criadoEm", "desc")
            );

            const snapshot = await getDocs(q);

            const lista = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
            }));

            setComunicacoes(lista);
        } catch (erro) {
            console.error("Erro ao carregar comunicações:", erro);
        }
    }

    async function carregarDestinatarios() {
        try {
            const lista = [];

            const clientesSnapshot = await getDocs(collection(db, "clientes"));

            clientesSnapshot.forEach((docSnap) => {
                const data = docSnap.data();

                if (data.email) {
                    lista.push({
                        id: docSnap.id,
                        tipo: "Cliente",
                        nome: data.nome || "Cliente sem nome",
                        email: data.email,
                    });
                }
            });

            const parceirosSnapshot = await getDocs(collection(db, "empresas"));

            parceirosSnapshot.forEach((docSnap) => {
                const data = docSnap.data();

                if (data.email) {
                    lista.push({
                        id: docSnap.id,
                        tipo: "Empresa",
                        nome: data.nomeEmpresa || data.nome || "Empresa sem nome",
                        email: data.email,
                    });
                }
            });

            setListaDestinatarios(lista);
        } catch (erro) {
            console.error("Erro ao carregar destinatários:", erro);
        }
    }

    function limparFormulario() {
        setAssunto("");
        setMensagem("");
        setPreviewHtml("");
        setHtmlTemplate("");
        setDestinatarios("todosClientes");
        setDestinatariosSelecionados([]);
        setPesquisaDestinatario("");
        setModoEdicao(false);
        setIdEdicao(null);
    }

    function abrirNovaComunicacao() {
        limparFormulario();
        setOpen(true);
    }

    function fecharDialog() {
        setOpen(false);
        setOpenConfirmacao(false);
        limparFormulario();
    }

    function validarFormulario() {
    if (!assunto.trim()) {
        setSnackbar({
            open: true,
            severity: "warning",
            message: "Introduza um assunto para a comunicação.",
        });
        return false;
    }

    if (!mensagem.trim()) {
        setSnackbar({
            open: true,
            severity: "warning",
            message: "Escreva o conteúdo da comunicação.",
        });
        return false;
    }

    if (
        destinatarios === "manual" &&
        destinatariosSelecionados.length === 0
    ) {
        setSnackbar({
            open: true,
            severity: "warning",
            message: "Selecione pelo menos um destinatário.",
        });
        return false;
    }

    return true;
}
    async function guardarRascunho() {
        if (!validarFormulario()) return;

        try {
            const dados = {
                assunto,
                mensagem,
                tipoDestinatarios: destinatarios,
                destinatariosSelecionados: destinatariosSelecionados.map((item) => ({
                    id: item.id,
                    nome: item.nome,
                    email: item.email,
                    tipo: item.tipo,
                })),
                htmlTemplate,
                estado: "rascunho",
            };

            if (modoEdicao && idEdicao) {
                await updateDoc(doc(db, "comunicacoes", idEdicao), dados);
            } else {
                await addDoc(collection(db, "comunicacoes"), {
                    ...dados,
                    criadoEm: serverTimestamp(),
                });
            }

            fecharDialog();
            carregarComunicacoes();
        } catch (erro) {
    console.error("Erro ao guardar rascunho:", erro);

    setSnackbar({
        open: true,
        severity: "error",
        message: "Erro ao guardar rascunho.",
    });
}
    }

    async function enviarComunicacao() {
    if (!validarFormulario()) return;

    let docRef = null;

    try {
        docRef = await addDoc(collection(db, "comunicacoes"), {
            assunto,
            mensagem,
            tipoDestinatarios: destinatarios,
            destinatariosSelecionados: destinatariosSelecionados.map((item) => ({
                id: item.id,
                nome: item.nome,
                email: item.email,
                tipo: item.tipo,
            })),
            htmlTemplate,
            estado: "a_enviar",
            criadoEm: serverTimestamp(),
        });

        const response = await fetch(CLOUD_FUNCTION_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                comunicacaoId: docRef.id,
            }),
        });

        const resultado = await response.json();

        if (!response.ok || !resultado.success) {
            throw new Error(resultado.error || "Erro ao enviar comunicação.");
        }

        setSnackbar({
            open: true,
            severity: "success",
            message: `Comunicação enviada. Enviados: ${resultado.enviados}. Falhados: ${resultado.falhados}.`,
        });

        fecharDialog();
        await carregarComunicacoes();
    } catch (erro) {
        console.error("Erro ao enviar comunicação:", erro);

        if (docRef) {
            await updateDoc(doc(db, "comunicacoes", docRef.id), {
                estado: "erro",
                erroEnvio: erro.message || "Erro ao enviar comunicação.",
            });
        }

        setSnackbar({
            open: true,
            severity: "error",
            message: erro.message || "Erro ao enviar comunicação.",
        });

        await carregarComunicacoes();
    }
}

    function importarTemplate(event) {
        const ficheiro = event.target.files?.[0];

        if (!ficheiro) return;

        const reader = new FileReader();

        reader.onload = (e) => {
            const conteudo = e.target.result || "";
            setHtmlTemplate(conteudo);
            setMensagem(conteudo);
            setPreviewHtml(conteudo);
        };

        reader.readAsText(ficheiro);
    }

    function editarComunicacao(item) {
        setModoEdicao(true);
        setIdEdicao(item.id);
        setAssunto(item.assunto || "");
        setMensagem(item.mensagem || "");
        setPreviewHtml(item.mensagem || "");
        setHtmlTemplate(item.htmlTemplate || "");
        setDestinatarios(item.tipoDestinatarios || "todosClientes");
        setDestinatariosSelecionados(item.destinatariosSelecionados || []);
        setOpen(true);
    }

    function duplicarComunicacao(item) {
        setModoEdicao(false);
        setIdEdicao(null);
        setAssunto(`${item.assunto || "Comunicação"} (Cópia)`);
        setMensagem(item.mensagem || "");
        setPreviewHtml(item.mensagem || "");
        setHtmlTemplate(item.htmlTemplate || "");
        setDestinatarios(item.tipoDestinatarios || "todosClientes");
        setDestinatariosSelecionados(item.destinatariosSelecionados || []);
        setOpen(true);
    }

    async function eliminarComunicacao() {
        if (!comunicacaoEliminar) return;

        try {
            await deleteDoc(doc(db, "comunicacoes", comunicacaoEliminar.id));
            setOpenEliminar(false);
            setComunicacaoEliminar(null);
            carregarComunicacoes();
        } catch (erro) {
    console.error("Erro ao eliminar comunicação:", erro);

    setSnackbar({
        open: true,
        severity: "error",
        message: "Erro ao eliminar comunicação.",
    });
}
    }

    function alternarDestinatario(item) {
        const jaSelecionado = destinatariosSelecionados.some(
            (destinatario) => destinatario.id === item.id && destinatario.tipo === item.tipo
        );

        if (jaSelecionado) {
            setDestinatariosSelecionados((atual) =>
                atual.filter(
                    (destinatario) =>
                        !(destinatario.id === item.id && destinatario.tipo === item.tipo)
                )
            );
        } else {
            setDestinatariosSelecionados((atual) => [...atual, item]);
        }
    }

    const estatisticas = {
        total: comunicacoes.length,
        enviadas: comunicacoes.filter((c) => c.estado === "enviado").length,
        rascunhos: comunicacoes.filter((c) => c.estado === "rascunho").length,
        erros: comunicacoes.filter(
            (c) => c.estado === "erro" || c.estado === "enviado_com_erros"
        ).length,
    };

    const comunicacoesFiltradas = comunicacoes.filter((item) => {
        const textoPesquisa = `${item.assunto || ""} ${item.tipoDestinatarios || ""} ${item.estado || ""}`.toLowerCase();

        const correspondePesquisa = textoPesquisa.includes(pesquisa.toLowerCase());

        const correspondeEstado =
            estadoFiltro === "todos" ||
            item.estado === estadoFiltro ||
            (estadoFiltro === "erro" && item.estado === "enviado_com_erros");

        return correspondePesquisa && correspondeEstado;
    });

    const destinatariosFiltrados = listaDestinatarios.filter((item) => {
        const texto = `${item.nome || ""} ${item.email || ""} ${item.tipo || ""}`.toLowerCase();
        return texto.includes(pesquisaDestinatario.toLowerCase());
    });

    const cards = [
        {
            titulo: "Enviadas",
            valor: estatisticas.enviadas,
            filtro: "enviado",
            icon: <MarkEmailReadRoundedIcon />,
            cor: "#16A34A",
            fundo: "#ECFDF5",
        },
        {
            titulo: "Rascunhos",
            valor: estatisticas.rascunhos,
            filtro: "rascunho",
            icon: <DraftsRoundedIcon />,
            cor: "#D97706",
            fundo: "#FEF3C7",
        },
        {
            titulo: "Erros",
            valor: estatisticas.erros,
            filtro: "erro",
            icon: <WarningAmberRoundedIcon />,
            cor: "#DC2626",
            fundo: "#FEF2F2",
        },
        {
            titulo: "Total",
            valor: estatisticas.total,
            filtro: "todos",
            icon: <EmailRoundedIcon />,
            cor: "#2563EB",
            fundo: "#EFF6FF",
        },
    ];

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper
    elevation={0}
    sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 5,
        overflow: "hidden",
        position: "relative",
        border: "1px solid rgba(226,232,240,0.9)",
        background:
            "linear-gradient(135deg, #0f172a 0%, #1e293b 48%, #f97316 130%)",
        color: "#ffffff",
        boxShadow: "0 18px 45px rgba(15,23,42,0.18)",
        "&::after": {
            content: '""',
            position: "absolute",
            right: -80,
            top: -80,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(249,115,22,0.22)",
        },
    }}
>
    <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={3}
        sx={{ position: "relative", zIndex: 1 }}
    >
        <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
                sx={{
                    width: 68,
                    height: 68,
                    bgcolor: "rgba(255,255,255,0.16)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.24)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <EmailRoundedIcon fontSize="large" />
            </Avatar>

            <Box>
                <Chip
                    label="Centro de emails"
                    size="small"
                    sx={{
                        mb: 1,
                        bgcolor: "rgba(255,255,255,0.14)",
                        color: "#ffffff",
                        fontWeight: 800,
                    }}
                />

                <Typography variant="h4" fontWeight={900}>
                    Comunicações
                </Typography>

                <Typography sx={{ color: "rgba(255,255,255,0.78)" }}>
                    Gere emails, rascunhos e envios para clientes e empresas.
                </Typography>
            </Box>
        </Stack>

        <Button
            variant="contained"
            size="large"
            startIcon={<AddRoundedIcon />}
            onClick={abrirNovaComunicacao}
            sx={{
                borderRadius: 999,
                px: 4,
                py: 1.5,
                fontWeight: 900,
                textTransform: "none",
                bgcolor: "#f97316",
                boxShadow: "0 12px 28px rgba(249,115,22,0.35)",
                "&:hover": {
                    bgcolor: "#ea580c",
                },
            }}
        >
            Nova Comunicação
        </Button>
    </Stack>
</Paper>
                </Grid>

                {cards.map((card) => (
                    <Grid item xs={12} md={3} key={card.titulo}>
                        <Paper
                            elevation={0}
                            onClick={() => setEstadoFiltro(card.filtro)}
                            sx={{
    p: 3,
    borderRadius: 5,
    border:
        estadoFiltro === card.filtro
            ? `2px solid ${card.cor}`
            : "1px solid #e5e7eb",
    cursor: "pointer",
    transition: "all 0.25s ease",
    background:
        estadoFiltro === card.filtro
            ? `linear-gradient(135deg, ${card.fundo} 0%, #ffffff 100%)`
            : "#ffffff",
    boxShadow:
        estadoFiltro === card.filtro
            ? "0 14px 34px rgba(15,23,42,0.10)"
            : "0 8px 24px rgba(15,23,42,0.05)",
    "&:hover": {
        transform: "translateY(-5px)",
        boxShadow: "0 18px 38px rgba(15,23,42,0.12)",
        borderColor: card.cor,
    },
}}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    sx={{
                                        bgcolor: card.fundo,
                                        color: card.cor,
                                        width: 56,
                                        height: 56,
                                    }}
                                >
                                    {card.icon}
                                </Avatar>

                                <Box>
                                    <Typography color="text.secondary">
                                        {card.titulo}
                                    </Typography>

                                    <Typography variant="h4" fontWeight={800}>
                                        {card.valor}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Paper
                elevation={0}
                sx={{
    mt: 3,
    p: { xs: 2, md: 3 },
    borderRadius: 5,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    boxShadow: "0 12px 32px rgba(15,23,42,0.06)",
}}
            >
                <Stack
    direction={{ xs: "column", md: "row" }}
    justifyContent="space-between"
    alignItems={{ xs: "stretch", md: "center" }}
    spacing={2}
    sx={{ mb: 3 }}
>
    <Box>
        <Typography variant="h5" fontWeight={900}>
            Comunicações recentes
        </Typography>

        <Typography variant="body2" color="text.secondary">
            Consulta, edita, duplica ou elimina comunicações guardadas.
        </Typography>
    </Box>

    <TextField
        size="small"
        placeholder="Pesquisar comunicação..."
        value={pesquisa}
        onChange={(e) => setPesquisa(e.target.value)}
        sx={{
            width: { xs: "100%", md: 340 },
            "& .MuiOutlinedInput-root": {
                borderRadius: 999,
                bgcolor: "#f8fafc",
            },
        }}
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <SearchRoundedIcon />
                </InputAdornment>
            ),
        }}
    />
</Stack>

                {comunicacoesFiltradas.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 5,
                            textAlign: "center",
                            borderRadius: 4,
                            border: "1px dashed #cbd5e1",
                            bgcolor: "#f8fafc",
                        }}
                    >
                        <EmailRoundedIcon sx={{ fontSize: 56, color: "#94a3b8", mb: 1 }} />

                        <Typography fontWeight={800}>
                            Nenhuma comunicação encontrada
                        </Typography>

                        <Typography color="text.secondary">
                            Tenta alterar a pesquisa ou o filtro selecionado.
                        </Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2}>
                        {comunicacoesFiltradas.map((item) => (
                            <Card
                                key={item.id}
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    border: "1px solid #e5e7eb",
                                    transition: "all .25s ease",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 10px 25px rgba(0,0,0,.08)",
                                        borderColor: "#2563eb",
                                    },
                                }}
                            >
                                <CardContent>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        spacing={2}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    bgcolor: "#EFF6FF",
                                                    color: "#2563EB",
                                                }}
                                            >
                                                <EmailRoundedIcon />
                                            </Avatar>

                                            <Box>
                                                <Typography fontWeight={800} fontSize={16}>
                                                    {item.assunto || "Sem assunto"}
                                                </Typography>

                                                <Typography variant="body2" color="text.secondary">
                                                    {nomeDestinatarios(item.tipoDestinatarios)}
                                                </Typography>

                                                <Typography variant="caption" color="text.secondary">
                                                    {formatarData(item.criadoEm)}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Chip
                                                label={textoEstado(item.estado)}
                                                color={corEstado(item.estado)}
                                                sx={{ fontWeight: 700 }}
                                            />

                                            <Tooltip title="Visualizar">
                                                <IconButton
                                                    sx={{
                                                        bgcolor: "#EFF6FF",
                                                        "&:hover": { bgcolor: "#DBEAFE" },
                                                    }}
                                                    onClick={() => {
                                                        setComunicacaoSelecionada(item);
                                                        setOpenVisualizar(true);
                                                    }}
                                                >
                                                    <VisibilityRoundedIcon color="primary" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Editar">
                                                <IconButton
                                                    sx={{
                                                        bgcolor: "#F5F3FF",
                                                        "&:hover": { bgcolor: "#EDE9FE" },
                                                    }}
                                                    onClick={() => editarComunicacao(item)}
                                                >
                                                    <EditRoundedIcon color="secondary" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Duplicar">
                                                <IconButton
                                                    sx={{
                                                        bgcolor: "#ECFDF5",
                                                        "&:hover": { bgcolor: "#D1FAE5" },
                                                    }}
                                                    onClick={() => duplicarComunicacao(item)}
                                                >
                                                    <ContentCopyRoundedIcon sx={{ color: "#16A34A" }} />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Eliminar">
                                                <IconButton
                                                    sx={{
                                                        bgcolor: "#FEF2F2",
                                                        "&:hover": { bgcolor: "#FEE2E2" },
                                                    }}
                                                    onClick={() => {
                                                        setComunicacaoEliminar(item);
                                                        setOpenEliminar(true);
                                                    }}
                                                >
                                                    <DeleteRoundedIcon color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Paper>

            <Dialog open={open} onClose={fecharDialog} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ pb: 1, bgcolor: "#ffffff" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: "#1976d2",
                                width: 56,
                                height: 56,
                            }}
                        >
                            <EmailRoundedIcon />
                        </Avatar>

                        <Box>
                            <Typography variant="h5" fontWeight={800}>
                                {modoEdicao ? "Editar Comunicação" : "Nova Comunicação"}
                            </Typography>

                            <Typography color="text.secondary">
                                Crie e envie comunicações para clientes e empresas.
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ bgcolor: "#f8fafc", p: { xs: 2, md: 3 } }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2, md: 4 },
                            borderRadius: 4,
                            border: "1px solid #e5e7eb",
                            bgcolor: "#ffffff",
                        }}
                    >
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Assunto"
                                    fullWidth
                                    value={assunto}
                                    onChange={(e) => setAssunto(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    label="Destinatários"
                                    fullWidth
                                    value={destinatarios}
                                    onChange={(e) => {
                                        setDestinatarios(e.target.value);
                                        if (e.target.value !== "manual") {
                                            setDestinatariosSelecionados([]);
                                        }
                                    }}
                                >
                                    <MenuItem value="todosClientes">👥 Todos os Clientes</MenuItem>
                                    <MenuItem value="todasEmpresas">🏢 Todas as Empresas</MenuItem>
                                    <MenuItem value="manual">🎯 Seleção Manual</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <input
                                    type="file"
                                    accept=".html"
                                    hidden
                                    ref={inputFile}
                                    onChange={importarTemplate}
                                />

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<UploadFileRoundedIcon />}
                                    onClick={() => inputFile.current.click()}
                                    sx={{
                                        height: "56px",
                                        borderRadius: 2,
                                        fontWeight: 800,
                                        textTransform: "none",
                                    }}
                                >
                                    Importar HTML
                                </Button>
                            </Grid>

                            {destinatarios === "manual" && (
                                <Grid item xs={12}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            border: "1px solid #e5e7eb",
                                            bgcolor: "#f8fafc",
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 800 }}>
                                            Selecionar destinatários
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            placeholder="Pesquisar cliente ou empresa..."
                                            value={pesquisaDestinatario}
                                            onChange={(e) => setPesquisaDestinatario(e.target.value)}
                                            sx={{ mb: 2 }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchRoundedIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {destinatariosSelecionados.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                                    Destinatários selecionados
                                                </Typography>

                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {destinatariosSelecionados.map((item) => (
                                                        <Chip
                                                            key={`${item.tipo}-${item.id}`}
                                                            label={item.nome}
                                                            color="primary"
                                                            onDelete={() => alternarDestinatario(item)}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}

                                        <Stack spacing={1} sx={{ maxHeight: 320, overflow: "auto" }}>
                                            {destinatariosFiltrados.map((item) => {
                                                const selecionado = destinatariosSelecionados.some(
                                                    (destinatario) =>
                                                        destinatario.id === item.id && destinatario.tipo === item.tipo
                                                );

                                                return (
                                                    <Paper
                                                        key={`${item.tipo}-${item.id}`}
                                                        variant="outlined"
                                                        onClick={() => alternarDestinatario(item)}
                                                        sx={{
                                                            p: 2,
                                                            borderRadius: 3,
                                                            cursor: "pointer",
                                                            bgcolor: selecionado ? "#EFF6FF" : "#ffffff",
                                                        }}
                                                    >
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Checkbox checked={selecionado} />

                                                            <Avatar>{item.nome?.charAt(0)}</Avatar>

                                                            <Box>
                                                                <Typography fontWeight={700}>{item.nome}</Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {item.email}
                                                                </Typography>
                                                                <Chip size="small" label={item.tipo} sx={{ mt: 1 }} />
                                                            </Box>
                                                        </Stack>
                                                    </Paper>
                                                );
                                            })}
                                        </Stack>
                                    </Paper>
                                </Grid>
                            )}

                            <Grid item xs={12} md={8}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                                    Corpo do email
                                </Typography>

                                <Editor
                                    apiKey="v3qbc6l20knf9y6vujrxw6yx83o4rlshkttuw87iv9zwfbct"
                                    value={mensagem}
                                    onEditorChange={(content) => {
                                        setMensagem(content);
                                        setPreviewHtml(content);
                                    }}
                                    init={{
                                        height: 500,
                                        menubar: true,
                                        plugins: [
                                            "advlist",
                                            "autolink",
                                            "lists",
                                            "link",
                                            "image",
                                            "charmap",
                                            "preview",
                                            "anchor",
                                            "searchreplace",
                                            "visualblocks",
                                            "code",
                                            "fullscreen",
                                            "insertdatetime",
                                            "media",
                                            "table",
                                            "help",
                                            "wordcount",
                                        ],
                                        toolbar:
                                            "undo redo | blocks | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image table | removeformat code preview",
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: 4,
                                        border: "1px solid #e5e7eb",
                                        bgcolor: "#f8fafc",
                                        position: "sticky",
                                        top: 16,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                                        Resumo
                                    </Typography>

                                    <Stack spacing={1.5}>
                                        <Chip label="Estado: Rascunho" color="warning" />

                                        <Typography variant="body2" color="text.secondary">
                                            Destinatários
                                        </Typography>

                                        <Typography sx={{ fontWeight: 800 }}>
                                            {nomeDestinatarios(destinatarios)}
                                        </Typography>

                                        {destinatarios === "manual" && (
                                            <Typography variant="body2" color="text.secondary">
                                                Selecionados: {destinatariosSelecionados.length}
                                            </Typography>
                                        )}

                                        <Divider />

                                        <Typography variant="body2" color="text.secondary">
                                            Template HTML
                                        </Typography>

                                        <Chip
                                            label={htmlTemplate ? "Importado" : "Não importado"}
                                            color={htmlTemplate ? "success" : "default"}
                                            variant={htmlTemplate ? "filled" : "outlined"}
                                        />

                                        <Divider />

                                        <Typography variant="body2" color="text.secondary">
                                            Assunto
                                        </Typography>

                                        <Typography sx={{ fontWeight: 800 }}>
                                            {assunto || "Sem assunto"}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 900 }}>
                                    Pré-visualização
                                </Typography>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        minHeight: 260,
                                        borderRadius: 3,
                                        bgcolor: "#ffffff",
                                        border: "1px solid #e5e7eb",
                                        overflow: "auto",
                                    }}
                                >
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: previewHtml || mensagem || "<p>Sem conteúdo ainda.</p>",
                                        }}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Paper>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, md: 3 },
                        py: 2,
                        bgcolor: "#f8fafc",
                        borderTop: "1px solid #e5e7eb",
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        onClick={fecharDialog}
                        sx={{
                            borderRadius: 999,
                            px: 3,
                            fontWeight: 800,
                            textTransform: "none",
                        }}
                    >
                        Cancelar
                    </Button>

                    <Stack direction="row" spacing={1.5}>
                        <Button
                            startIcon={<SaveRoundedIcon />}
                            variant="outlined"
                            onClick={guardarRascunho}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                fontWeight: 800,
                                textTransform: "none",
                            }}
                        >
                            Guardar rascunho
                        </Button>

                        <Button
                            startIcon={<SendRoundedIcon />}
                            color="success"
                            variant="contained"
                            onClick={() => setOpenConfirmacao(true)}
                            sx={{
                                borderRadius: 999,
                                px: 3,
                                fontWeight: 800,
                                textTransform: "none",
                            }}
                        >
                            Enviar Comunicação
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openConfirmacao}
                onClose={() => setOpenConfirmacao(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar envio</DialogTitle>

                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Tem a certeza que pretende enviar esta comunicação?
                    </Typography>

                    <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 3 }}>
                        <Typography>
                            <strong>Assunto:</strong> {assunto || "-"}
                        </Typography>

                        <Typography sx={{ mt: 1 }}>
                            <strong>Destinatários:</strong> {nomeDestinatarios(destinatarios)}
                        </Typography>
                    </Paper>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenConfirmacao(false)}>Cancelar</Button>

                    <Button color="success" variant="contained" onClick={enviarComunicacao}>
                        Confirmar envio
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openVisualizar}
                onClose={() => setOpenVisualizar(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Visualizar Comunicação</DialogTitle>

                <DialogContent>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                        {comunicacaoSelecionada?.assunto}
                    </Typography>

                    <Chip
                        label={textoEstado(comunicacaoSelecionada?.estado)}
                        color={corEstado(comunicacaoSelecionada?.estado)}
                        sx={{ mb: 3 }}
                    />

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: "1px solid #e5e7eb",
                            borderRadius: 3,
                            minHeight: 300,
                        }}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: comunicacaoSelecionada?.mensagem || "",
                            }}
                        />
                    </Paper>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenVisualizar(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openEliminar} onClose={() => setOpenEliminar(false)}>
                <DialogTitle>Eliminar comunicação</DialogTitle>

                <DialogContent>
                    <Typography>Tem a certeza que pretende eliminar a comunicação:</Typography>

                    <Typography fontWeight={700} sx={{ mt: 2 }}>
                        {comunicacaoEliminar?.assunto}
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenEliminar(false)}>Cancelar</Button>

                    <Button color="error" variant="contained" onClick={eliminarComunicacao}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
    open={snackbar.open}
    autoHideDuration={4000}
    onClose={() =>
        setSnackbar({
            ...snackbar,
            open: false,
        })
    }
    anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
    }}
>
    <Alert
        severity={snackbar.severity}
        variant="filled"
        onClose={() =>
            setSnackbar({
                ...snackbar,
                open: false,
            })
        }
        sx={{ width: "100%" }}
    >
        {snackbar.message}
    </Alert>
</Snackbar>
        </Box>
    );
}
